import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApiService, AiPerformanceSnapshotEvaluation } from '../../services/adminApiService';
import SideBreakdownSummary from '../trading/SideBreakdownSummary';
import {
  computeSideWinLossFromSnapshotTrades,
  directionFromSnapshotRow,
} from '../../utils/tradeWindowEvaluation';

const WINDOW_OPTIONS: { value: string; label: string }[] = [
  { value: '5', label: 'Last 5' },
  { value: '10', label: 'Last 10' },
  { value: '12', label: 'Last 12' },
  { value: '20', label: 'Last 20' },
  { value: 'all', label: 'All loaded' },
];

function money(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  const prefix = v >= 0 ? '+' : '-';
  return `${prefix}$${Math.abs(v).toFixed(2)}`;
}

function pct(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  const prefix = v >= 0 ? '+' : '-';
  return `${prefix}${Math.abs(v).toFixed(1)}%`;
}

function pnlCell(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  if (v >= 0) {
    return v >= 0.01 ? `+${v.toFixed(2)}` : `+${v.toFixed(4)}`.replace(/\.?0+$/, '');
  }
  const a = Math.abs(v);
  return a >= 0.01 ? `-${a.toFixed(2)}` : `-${a.toFixed(4)}`.replace(/\.?0+$/, '');
}

export interface AiTunePerformanceSnapshotProps {
  strategyId: number;
  /** Reload when tab becomes visible */
  active: boolean;
}

const AiTunePerformanceSnapshot: React.FC<AiTunePerformanceSnapshotProps> = ({
  strategyId,
  active,
}) => {
  const [window, setWindow] = useState('12');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<AiPerformanceSnapshotEvaluation | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(true);

  const load = useCallback(async () => {
    if (!strategyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.getAiPerformanceSnapshot(strategyId, window);
      if (res.success && res.data?.evaluation) {
        setEvaluation(res.data.evaluation);
      } else {
        setEvaluation(null);
        setError(res.error || 'Failed to load performance snapshot');
      }
    } catch (e: unknown) {
      setEvaluation(null);
      setError(e instanceof Error ? e.message : 'Failed to load performance snapshot');
    } finally {
      setLoading(false);
    }
  }, [strategyId, window]);

  useEffect(() => {
    if (active) {
      load();
    }
  }, [active, load]);

  const sideBreakdown = useMemo(
    () => computeSideWinLossFromSnapshotTrades(evaluation?.trades || []),
    [evaluation?.trades]
  );

  if (!active) return null;

  return (
    <div>
      <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
        Read-only KuCoin Live closed-trade evaluation. Same metrics as Full Analytics — no config changes.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Window:</span>
        {WINDOW_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setWindow(opt.value)}
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              border: window === opt.value ? '2px solid #7c3aed' : '1px solid #e5e7eb',
              background: window === opt.value ? '#faf5ff' : '#fff',
              color: window === opt.value ? '#6d28d9' : '#374151',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #c4b5fd',
            background: '#faf5ff',
            color: '#6d28d9',
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, fontSize: 13, color: '#856404', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading && !evaluation && (
        <p style={{ fontSize: 13, color: '#6b7280' }}>Loading KuCoin performance…</p>
      )}

      {!loading && evaluation && (
        <>
          {evaluation.insufficientData && (
            <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 12 }}>
              Need at least {evaluation.minTradesRequired} closes in this window for future performance-based tuning ({evaluation.actualCount} available).
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 14 }}>
            {[
              { label: 'Gross P&L', value: money(Number(evaluation.grossPnl)), color: Number(evaluation.grossPnl) >= 0 ? '#198754' : '#dc3545' },
              { label: 'Total fees', value: money(Number(evaluation.totalFees)), color: '#dc3545' },
              { label: 'Net P&L', value: money(Number(evaluation.netPnl)), color: Number(evaluation.netPnl) >= 0 ? '#198754' : '#dc3545' },
              { label: evaluation.orderSize ? `Return ($${Number(evaluation.orderSize).toFixed(0)})` : 'Return', value: evaluation.returnOnOrderSize != null ? pct(Number(evaluation.returnOnOrderSize)) : '—', color: Number(evaluation.returnOnOrderSize ?? 0) >= 0 ? '#198754' : '#dc3545' },
              { label: 'Win rate', value: `${Number(evaluation.winRate).toFixed(1)}%`, color: '#086afb' },
              { label: 'W / L', value: `${evaluation.winCount} / ${evaluation.lossCount}`, color: '#212529' },
            ].map((cell) => (
              <div key={cell.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{cell.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: cell.color }}>{cell.value}</div>
              </div>
            ))}
          </div>

          <SideBreakdownSummary breakdown={sideBreakdown} />

          <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', marginBottom: 8 }}>{evaluation.title}</div>
          <div style={{ overflowX: 'auto', marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Date/Time</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Trade</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>P&L ($)</th>
                </tr>
              </thead>
              <tbody>
                {(evaluation.trades || []).map((t, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: 8, color: '#495057' }}>{t.closedDisplay || '—'}</td>
                    <td style={{ padding: 8 }}>
                      <SnapshotTradeLabel label={t.label || t.symbol} side={t.side} />
                    </td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, color: Number(t.netPnl ?? t.pnlGross) >= 0 ? '#198754' : '#dc3545' }}>
                      {pnlCell(Number(t.netPnl ?? t.pnlGross))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() => setShowBreakdown((s) => !s)}
            style={{ border: 0, background: 'transparent', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: showBreakdown ? 10 : 0 }}
          >
            {showBreakdown ? '▼' : '▶'} Per-coin breakdown
          </button>

          {showBreakdown && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>By coin</div>
                {(evaluation.byCoin || []).length === 0 ? (
                  <div style={{ fontSize: 12, color: '#adb5bd' }}>No data</div>
                ) : (
                  (evaluation.byCoin || []).map((c) => (
                    <div key={c.symbol} style={{ fontSize: 12, marginBottom: 6, color: '#495057' }}>
                      <strong>{c.symbol}</strong> · {c.tradeCount} trades · net {money(Number(c.netPnl))} · W/L {c.winCount}/{c.lossCount}
                    </div>
                  ))
                )}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>By coin + side</div>
                {(evaluation.byCoinSide || []).length === 0 ? (
                  <div style={{ fontSize: 12, color: '#adb5bd' }}>No data</div>
                ) : (
                  (evaluation.byCoinSide || []).map((c) => (
                    <div key={`${c.symbol}-${c.side}`} style={{ fontSize: 12, marginBottom: 6, color: '#495057' }}>
                      <strong>{c.label || `${c.symbol} ${c.side}`}</strong> · {c.tradeCount} · net {money(Number(c.netPnl))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function SnapshotTradeLabel({ label, side }: { label?: string; side?: string }) {
  const direction = directionFromSnapshotRow(label, side);
  if (direction === 'UNKNOWN') {
    return <span>{label || '—'}</span>;
  }
  const base = (label || '').replace(/\s+(LONG|SHORT)$/i, '').trim();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{base}</span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 4,
          background: direction === 'LONG' ? '#d1e7dd' : '#f8d7da',
          color: direction === 'LONG' ? '#0f5132' : '#842029',
        }}
      >
        {direction}
      </span>
    </span>
  );
}

export default AiTunePerformanceSnapshot;
